import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title = 'KO Holistic Health | Reiki, Tarot & Holistic Healing Sydney',
  description = 'Sydney\'s trusted holistic health practitioner offering Reiki healing, tarot readings, soul coaching, chakra healing, meditation classes & spiritual guidance. Book your session with Kirstie Warburton today.',
  keywords = 'reiki healing sydney, tarot reading sydney, soul coaching, meditation classes sydney, chakra healing, spiritual guidance sydney, holistic health practitioner, energy healing, crystal healing, reiki master sydney, holistic wellness, kirstie warburton, space clearing, restorative reiki classes',
  image = '/Logo.webp',
  url = '',
  type = 'website',
  author = 'Kirstie Warburton',
  schema = null,
  breadcrumbs = null,
  noindex = false
}) => {
  const siteUrl = 'https://koholistichealth.com';
  const location = useLocation();
  const currentPath = url || location.pathname;
  const fullUrl = currentPath ? `${siteUrl}${currentPath}` : siteUrl;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    "name": "KO Holistic Health",
    "alternateName": "KO Holistic Health and Wellbeing",
    "description": "Sydney's trusted holistic health practitioner offering Reiki healing, tarot readings, soul coaching, chakra healing, meditation classes and spiritual guidance.",
    "url": siteUrl,
    "logo": `${siteUrl}/Logo.webp`,
    "image": `${siteUrl}/Logo.webp`,
    "telephone": "+61439386551",
    "email": "info@koholistichealth.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Sydney",
      "addressRegion": "NSW",
      "postalCode": "2000",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -33.8688,
      "longitude": 151.2093
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+61439386551",
      "contactType": "Customer Service",
      "availableLanguage": ["English"],
      "areaServed": "AU"
    },
    "founder": {
      "@type": "Person",
      "name": "Kirstie Warburton",
      "jobTitle": "Holistic Health Practitioner & Reiki Master Teacher"
    },
    "priceRange": "$$",
    "currenciesAccepted": "AUD",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer",
    "areaServed": {
      "@type": "City",
      "name": "Sydney",
      "containedInPlace": {
        "@type": "State",
        "name": "New South Wales"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Holistic Health Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Intuitive Reiki Healing", "description": "Energy healing for restoration and balance" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Intuitive Tarot Readings", "description": "Clarity and guidance for your life journey" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Soul Alignment Coaching", "description": "Align with your true purpose" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Chakra Healing", "description": "Balance your energy centres" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Space Clearing", "description": "Cleanse and harmonize your environment" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Restorative Reiki Classes", "description": "A sanctuary for healing and self-discovery" } }
      ]
    },
    "sameAs": [
      "https://www.instagram.com/koholistichealth",
      "https://www.facebook.com/koholistichealth"
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${siteUrl}${crumb.path}`
    }))
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="KO Holistic Health" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional SEO */}
      <meta property="og:locale" content="en_AU" />
      <link rel="canonical" href={fullUrl} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="language" content="English" />
      <meta name="geo.region" content="AU-NSW" />
      <meta name="geo.placename" content="Sydney" />
      <meta name="geo.position" content="-33.8688;151.2093" />
      <meta name="ICBM" content="-33.8688, 151.2093" />

      {/* Business Schema */}
      <script type="application/ld+json">
        {JSON.stringify(businessSchema)}
      </script>

      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Additional Page-Specific Schema */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
